import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatRupiah } from '../lib/api';
import Navbar from '../components/Navbar';
import { CheckCircle, Clock, ArrowRight, ArrowSquareOut, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingVideo from '../components/LoadingVideo';

export default function PaymentStatus() {
  const { orderId } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await api.get(`/checkout/status/${orderId}`);
      setStatus(res.data);
    } catch {
      setStatus({ status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    checkStatus();
    const iv = setInterval(() => {
      setStatus((prev) => {
        if (prev && ['completed', 'canceled', 'error'].includes(prev.status)) {
          return prev;
        }
        checkStatus();
        return prev;
      });
    }, 5000);
    return () => clearInterval(iv);
  }, [checkStatus]);

  const simulatePayment = async () => {
    try {
      await api.post(`/dev/simulate-payment/${orderId}`);
      toast.success('Pembayaran simulasi berhasil!');
      checkStatus();
    } catch (e) {
      toast.error('Gagal simulasi');
    }
  };

  const cancelPayment = async () => {
    if (canceling) return;
    setCanceling(true);
    try {
      await api.post(`/checkout/cancel/${orderId}`);
      toast.success('Transaksi berhasil dibatalkan');
      await checkStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membatalkan transaksi');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-16">
          <LoadingVideo text="Memuat transaksi..." />
        </main>
      </div>
    );
  }

  const isCompleted = status?.status === 'completed';
  const isCanceled = status?.status === 'canceled';
  const isError = status?.status === 'error';
  const qrisPayload = status?.qris_payload;
  // Use a public QR code generation API for immediate display
  const qrImageUrl = qrisPayload 
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrisPayload)}`
    : null;

  const fallbackPayUrl = status?.payment_url || (
    status?.txn_id
      ? `https://app.pakasir.com/pay-v2/${status.txn_id}?qris_only=1`
      : `https://app.pakasir.com/pay/traktirkan/${status?.amount}?order_id=${orderId}&qris_only=1`
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 py-6 md:py-10" data-testid="payment-status-page">
        <div className="border-2 border-foreground bg-white shadow-brutal-lg p-6 md:p-8 text-center flex flex-col overflow-hidden">
          {isError ? (
            <div className="py-4">
              <div className="mb-8 border-4 border-foreground bg-white shadow-brutal-lg overflow-hidden max-w-xs mx-auto">
                <img src="/error.png" alt="Error" className="w-full h-auto" />
              </div>
              <h1 className="font-display text-3xl font-bold mb-4">Terjadi Kesalahan</h1>
              <p className="text-muted-foreground mb-8 text-sm">Transaksi tidak ditemukan, telah kedaluwarsa, atau terjadi kesalahan sistem.</p>
              <Link to="/" className="inline-block border-2 border-foreground bg-primary text-white px-8 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
                Kembali ke Beranda
              </Link>
            </div>
          ) : isCanceled ? (
            <div className="py-4">
              <div className="w-20 h-20 border-2 border-foreground bg-muted mx-auto mb-4 flex items-center justify-center shadow-brutal">
                <XCircle size={44} weight="bold" className="text-destructive" />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-2 text-destructive">Dibatalkan</p>
              <h1 className="font-display text-3xl font-bold mb-2">Transaksi Dibatalkan</h1>
              <p className="text-muted-foreground mb-6 text-sm">
                Transaksi dengan Order ID <span className="font-mono font-bold">{orderId}</span> telah dibatalkan atau kedaluwarsa.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-6 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                Kembali ke Beranda <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          ) : isCompleted ? (
            <>
              <div className="w-28 h-28 border-2 border-foreground bg-white mx-auto mb-4 flex items-center justify-center shadow-brutal overflow-hidden">
                <img src="/berhasil.png" alt="Berhasil" className="w-full h-full object-contain" />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-2">Berhasil</p>
              <h1 className="font-display text-3xl font-bold mb-2" data-testid="payment-success-title">Terima Kasih! 🎉</h1>
              <p className="text-muted-foreground mb-6">Traktiran kamu sebesar <strong>{formatRupiah(status.amount)}</strong> sudah diterima.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-6 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                data-testid="payment-done-btn"
              >
                Selesai <ArrowRight size={18} weight="bold" />
              </Link>
            </>
          ) : (
            <div className="flex flex-col min-h-0">
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-2">Instruksi Pembayaran</p>
              <h1 className="font-display text-2xl font-bold mb-1">Scan QRIS untuk Bayar</h1>
              <p className="text-sm text-muted-foreground mb-6">Silakan selesaikan pembayaran sebelum kedaluwarsa</p>

              {/* QRIS Display Area */}
              <div className="border-4 border-foreground bg-white p-4 shadow-brutal-md inline-block mx-auto mb-6 transition-transform hover:scale-[1.02]">
                {qrImageUrl ? (
                  <img 
                    src={qrImageUrl} 
                    alt="QRIS Payment" 
                    className="w-48 h-48 md:w-64 md:h-64 object-contain mx-auto"
                  />
                ) : (
                  <div className="w-48 h-48 md:w-64 md:h-64 bg-muted flex flex-col items-center justify-center gap-2">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent animate-spin rounded-full"></div>
                    <p className="text-[10px] font-bold uppercase">Memuat QRIS...</p>
                  </div>
                )}
                <div className="mt-4 border-t-2 border-foreground pt-3">
                  <p className="font-black text-xl md:text-2xl font-display">{formatRupiah(status?.amount)}</p>
                </div>
              </div>

              {/* Status Info */}
              <div className="space-y-4 text-left">
                <div className="border-2 border-foreground bg-accent/10 p-5 space-y-3">
                  <div className="flex justify-between items-start text-xs">
                    <div className="flex flex-col gap-0.5">
                      <span className="opacity-60 font-bold uppercase tracking-widest text-[10px]">Nominal</span>
                      <span className="font-bold opacity-60 text-[9px] italic">
                        {status?.unit_name === 'Dana' ? `(${status?.unit_name})` : `(${status?.qty}× ${status?.unit_name})`}
                      </span>
                    </div>
                    <span className="font-bold">{formatRupiah(status?.net_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="opacity-60 font-bold uppercase tracking-widest text-[10px]">Biaya Admin</span>
                    <span className="font-bold text-primary">+{formatRupiah(status?.platform_fee)}</span>
                  </div>
                  <div className="pt-3 border-t-2 border-foreground/20 flex justify-between items-center">
                    <span className="font-black text-xs uppercase tracking-widest">Total Bayar</span>
                    <span className="font-display text-2xl font-black text-primary drop-shadow-sm">{formatRupiah(status?.amount)}</span>
                  </div>
                </div>

                <div className="border-2 border-foreground bg-white p-3">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="opacity-50 font-bold uppercase">ORDER ID</span>
                    <span className="font-mono font-bold">{orderId}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <p className="text-[10px] text-center text-muted-foreground italic leading-relaxed">
                    Scan kode QR di atas menggunakan aplikasi mobile banking (BCA, Mandiri, BRI, dll) atau e-wallet (Dana, OVO, GoPay, ShopeePay).
                  </p>
                  
                  {/* Payment Guide Image */}
                  <div className="border-2 border-foreground bg-white p-2 shadow-brutal-sm">
                    <img src="/payment-guide.webp" alt="Cara Pembayaran" className="w-full h-auto" />
                  </div>

                  <div className="flex flex-col items-center gap-2">
                    <button 
                      type="button"
                      onClick={() => window.open(fallbackPayUrl, '_blank')}
                      className="text-[10px] underline font-bold opacity-60 hover:opacity-100"
                    >
                      Bermasalah dengan QRIS di atas? Klik di sini untuk cara lain.
                    </button>

                    <button
                      type="button"
                      onClick={cancelPayment}
                      disabled={canceling}
                      className="text-[10px] underline font-bold text-destructive opacity-70 hover:opacity-100 disabled:opacity-40"
                    >
                      {canceling ? 'Membatalkan...' : 'Batalkan Pembayaran'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
